import React, { useState, useCallback, useEffect, useMemo } from "react";

// MUI
import Button from "@mui/material/Button";

// React-Icons
import { useTranslation } from "react-i18next";
import { BsThreeDots } from "react-icons/bs";
import { IoBookOutline } from "react-icons/io5";
import { CiDumbbell } from "react-icons/ci";
import { FaArrowLeft } from "react-icons/fa";

// Contexts
import { StudyPathSideBarContext } from "./context";

// Components
import MUISegmented from "../../components/MUISegmented";
import Spinner from "../../components/spinner";
import Divider from "../../components/divider";
import Journey from "./Views/Journey";
import Practice from "./Views/Practice";

// Styles
import styles from "./sidebar.module.scss";

// Utils
import { courseUrl } from "../../utils/urls";
import IconButton from "@mui/material/IconButton";

const StudyPathSideBar = ({ courseId = null }) => {
  const { t } = useTranslation();

  // State
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [view, setView] = useState('journey');

  // Methods
  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    try {
      setLoading(true);
      const options = { method: "GET", headers: { "Accept": "application/json" } }

      const response = await fetch(courseUrl(courseId), options);
      if (!response.ok) {
        throw new Error("Failed to fetch course");
      }

      const data = await response.json();
      setCourse(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  // Handle
  const handleBack = useCallback(() => {
    Turbo.visit('/courses');
  }, [])

  // Context
  const contextValue = useMemo(() => ({
    course,
  }), [course]);

  // Render
  return (
    <StudyPathSideBarContext.Provider value={contextValue}>
      <div className={styles.container}>
        {loading && <Spinner />}

        <div className={styles.topContainer}>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleBack}
            startIcon={<FaArrowLeft className='w-6 h-6' />}
            sx={{
              width: '200px',
            }}
          >
            {t('studyPathSideBar.back')}
          </Button>

          <Divider className='my-4' />

          <div className={styles.header}>
            <p className='text-xl text-black dark:text-white' dir="auto">
              {course?.name || t('studyPathSideBar.title')}
            </p>

            <div className='flex flex-row items-center gap-2'>
              <IconButton
                color='primary'
                classes={{
                  root: styles.dotsButton
                }}
              >
                <BsThreeDots className='w-6 h-6' />
              </IconButton>
            </div>
          </div>


          <MUISegmented
            fullWidth
            options={[
              {
                value: 'journey',
                label: (
                  <span className='flex flex-row items-center gap-2'>
                    <IoBookOutline className='w-6 h-6' />

                    {t('masteryMap.actions.journey')}
                  </span>
                )
              },
              {
                value: 'practice',
                label: (
                  <span className='flex flex-row items-center gap-2'>
                    <CiDumbbell className='w-6 h-6' />

                    {t('masteryMap.actions.practice')}
                  </span>
                )
              },
            ]}
            value={view}
            onChange={setView}
            sx={{
              mb: 2,
            }}
          />
        </div>

        <div className={styles.contentContainer}>
          {view === 'journey' && <Journey courseId={courseId} />}
          {view === 'practice' && <Practice courseId={courseId} />}
        </div>
      </div>
    </StudyPathSideBarContext.Provider>
  );
};

export default StudyPathSideBar;

